import React, { useState, useEffect, useRef } from 'react';
import { Modal, Input, Textarea, Btn, Field } from './UI';
import { uploadImage } from '../../services/api';

const EMPTY = {
  nombre: '', precio: '', categoria: '',
  descripcion: '', imagen: '', stock: '', destacado: false,
};

export default function ProductForm({ open, onClose, onSave, initial, loading }) {
  const [f, setF]             = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver]   = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    setF(initial ? {
      nombre:      initial.nombre      || '',
      precio:      initial.precio      ?? '',
      categoria:   initial.categoria   || '',
      descripcion: initial.descripcion || '',
      imagen:      initial.imagen      || '',
      stock:       initial.stock       ?? '',
      destacado:   !!initial.destacado,
    } : EMPTY);
  }, [initial, open]);

  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  /* ── Upload handler ───────────────────────────────────────────────────── */
  const handleFile = async (file) => {
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowed.includes(file.type)) {
      alert('Solo se permiten imágenes JPG, PNG, GIF o WEBP.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('La imagen no puede superar los 5 MB.');
      return;
    }
    setUploading(true);
    try {
      const url = await uploadImage(file);
      set('imagen', url);
    } catch (err) {
      alert('Error al subir la imagen: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleInputFile = (e) => handleFile(e.target.files?.[0]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  /* ── Submit ───────────────────────────────────────────────────────────── */
  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      nombre:      f.nombre.trim(),
      precio:      parseFloat(f.precio) || 0,
      categoria:   f.categoria.trim(),
      descripcion: f.descripcion.trim(),
      imagen:      f.imagen.trim(),
      stock:       Math.max(0, parseInt(f.stock) || 0),
      destacado:   f.destacado,
    });
  };

  return (
    <Modal open={open} onClose={onClose} title={initial ? 'Editar producto' : 'Nuevo producto'}>
      <form onSubmit={handleSubmit}>

        <Input
          label="Nombre del producto *"
          value={f.nombre}
          onChange={e => set('nombre', e.target.value)}
          placeholder="Ej: Nike Air Max 90"
          required
        />

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
          <Input
            label="Precio *"
            type="number"
            min="0"
            step="0.01"
            value={f.precio}
            onChange={e => set('precio', e.target.value)}
            placeholder="24999"
            required
          />
          <Input
            label="Stock (opcional)"
            type="number"
            min="0"
            value={f.stock}
            onChange={e => set('stock', e.target.value)}
            placeholder="sin límite"
          />
          <Input
            label="Categoría"
            value={f.categoria}
            onChange={e => set('categoria', e.target.value)}
            placeholder="Running, Casual..."
          />
        </div>

        <Textarea
          label="Descripción"
          value={f.descripcion}
          onChange={e => set('descripcion', e.target.value)}
          placeholder="Describí el producto..."
        />

        {/* ── Imagen del producto ─────────────────────────────────────── */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display:'block', color:'rgba(255,255,255,0.55)', fontSize:13, fontWeight:500, marginBottom:8 }}>
            Imagen del producto
          </label>

          {/* Zona de upload (click/tap + drag & drop) */}
          <div
            onClick={() => fileRef.current?.click()}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            style={{
              border: `2px dashed ${dragOver ? '#7c3aed' : 'rgba(255,255,255,0.15)'}`,
              borderRadius: 12,
              padding: '28px 20px',
              textAlign: 'center',
              cursor: 'pointer',
              background: dragOver ? 'rgba(124,58,237,0.08)' : 'rgba(255,255,255,0.03)',
              transition: 'all 0.2s',
              marginBottom: 8,
            }}
          >
            {uploading ? (
              <div style={{ color:'rgba(255,255,255,0.5)', fontSize:14 }}>
                ⏳ Subiendo imagen...
              </div>
            ) : (
              <>
                <div style={{ fontSize: 32, marginBottom: 8 }}>📷</div>
                <div style={{ color:'rgba(255,255,255,0.75)', fontSize:14, marginBottom:4, fontWeight: 500 }}>
                  Tocá para seleccionar una foto
                </div>
                <div style={{ color:'rgba(255,255,255,0.4)', fontSize:12, marginBottom:2 }}>
                  (o arrastrá una imagen desde tu PC)
                </div>
                <div style={{ color:'rgba(255,255,255,0.3)', fontSize:11 }}>
                  JPG · PNG · GIF · WEBP — máx. 5 MB
                </div>
              </>
            )}
          </div>

          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            style={{ display:'none' }}
            onChange={handleInputFile}
          />

          {f.imagen && !uploading && (
            <p style={{ color:'rgba(255,255,255,0.35)', fontSize:12, margin:'4px 0 0', wordBreak:'break-all' }}>
              ✅ {f.imagen.split('/').pop()}
            </p>
          )}

          {/* Preview */}
          {f.imagen && !uploading && (
            <div style={{
              marginTop: 10, borderRadius: 10, overflow:'hidden',
              border:'1px solid rgba(255,255,255,0.1)', position:'relative',
            }}>
              <img
                src={f.imagen}
                alt="preview"
                style={{ width:'100%', height:160, objectFit:'cover', display:'block' }}
                onError={e => { e.target.style.display = 'none'; }}
              />
              <button
                type="button"
                onClick={() => set('imagen', '')}
                style={{
                  position:'absolute', top:8, right:8,
                  background:'rgba(0,0,0,0.6)', border:'none',
                  borderRadius:6, color:'#fff', fontSize:12,
                  cursor:'pointer', padding:'4px 9px',
                }}
              >
                ✕ Quitar
              </button>
            </div>
          )}
        </div>

        <Field style={{ marginBottom:20 }}>
          <label style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer' }}>
            <input
              type="checkbox"
              checked={f.destacado}
              onChange={e => set('destacado', e.target.checked)}
              style={{ width:17, height:17, accentColor:'#7c3aed', cursor:'pointer' }}
            />
            <span style={{ color:'rgba(255,255,255,0.7)', fontSize:14 }}>
              Marcar como destacado ⭐
            </span>
          </label>
        </Field>

        <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
          <Btn variant="ghost" type="button" onClick={onClose}>Cancelar</Btn>
          <Btn type="submit" loading={loading || uploading}>
            {initial ? 'Guardar cambios' : 'Crear producto'}
          </Btn>
        </div>
      </form>
    </Modal>
  );
}
